import { useState, useEffect } from 'react';
import { Form, message, Modal } from 'antd';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getCurrentTimeInTimezone } from '../../../../utils/timezoneUtils';
import {
    PickupAppointmentDrawerProps,
    PickupAppointmentFormData,
    PickupAppointmentDrawerState,
    DEFAULT_MANUAL_REASON,
    SPECIAL_MANUAL_REASONS
} from '../types/PickupAppointmentType';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { GET_PICKUP_APPOINTMENT_DETAIL, PickupAppointmentDetailResponse } from '../graphql/queries/getPickupAppointmentDetail';
import { SAVE_PICKUP_APPOINTMENT, SavePickupAppointmentResponse, SavePickupAppointmentInput, PickupAppointmentManualReason } from '../graphql/mutations/savePickupAppointment';
import { CANCEL_PICKUP_APPOINTMENT, CancelPickupAppointmentResponse } from '../graphql/mutations/cancelPickupAppointment';
import { allChannelLiteResponse, GET_ALL_CHANNEL_LITE } from '../../../../graphql/queries/getChannelAll';
import { 
    GET_ALL_STATES, 
    GET_CITIES_BY_STATE, 
    GET_ZIPCODES_BY_STATE_AND_CITY,
    GET_ZIPCODE_DETAIL,
    GetAllStatesResponse,
    GetCitiesByStateResponse,
    GetZipcodesByStateAndCityResponse,
    GetZipcodeDetailResponse,
    ZipcodeType
} from '../graphql/queries/getLocationData';
import { GET_WAREHOUSE_BY_ZIPCODE, WarehouseByZipcodeData, WarehouseByZipcodeVariables } from '../graphql/queries/getWarehouseByZipcode';
import { GET_PICKUP_EXCEPTION_REASONS, GetPickupExceptionReasonsResponse } from '../graphql/queries/getPickupExceptionReasons';
import { CHECK_APPOINTMENT_FOR_DATE, CheckAppointmentForDateResponse, CheckAppointmentForDateVariables } from '../graphql/queries/checkExistsAppointmentForDate';
import { useTranslation } from 'react-i18next';
import { GET_COOPERATION_WAREHOUSE_BY_ID, FindCooperationWarehouseByIdResponse } from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { GET_PICKUP_APPOINTMENT_LISTING, PickupAppointmentListingResponse } from '../graphql/queries/getPickupAppointmentListing';

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

export const usePickupAppointmentDrawer = ({
    visible,
    onClose,
    appointmentId,
    mode,
    onSuccess,
    listPickupDate,
    canCreateAppointment: externalCanCreateAppointment,
    reviewConfirmMode,
    initialRepickupSourceId
}: PickupAppointmentDrawerProps) => {
    const [form] = Form.useForm();
    const [state, setState] = useState<PickupAppointmentDrawerState>({
        loading: false,
        appointment: null,
        states: [],
        cities: [],
        zipcodes: [],
        selectedState: '',
        selectedCity: '',
        selectedZipcode: '',
        statesLoading: false,
        citiesLoading: false,
        zipcodesLoading: false
    });
    const {t} = useTranslation();
    // State for appointment deadline and minimum pickup date
    const [appointmentDeadlineTime, setAppointmentDeadlineTime] = useState<string | null>(null);
    const [minimumPickupDate, setMinimumPickupDate] = useState<dayjs.Dayjs>(() => dayjs());
    
    // 使用传入的 canCreateAppointment 值，如果没有传入则使用内部计算的值
    const [internalCanCreateAppointment, setInternalCanCreateAppointment] = useState<boolean>(true);
    const canCreateAppointment = externalCanCreateAppointment !== undefined ? externalCanCreateAppointment : internalCanCreateAppointment;
    
    // 标记是否刚刚更新成功，用于抑制zipcode提示
    const [justUpdated, setJustUpdated] = useState(false);

    const [cancelDrawerModalVisible, setCancelDrawerModalVisible] = useState(false);
    const [cancelDrawerVoucherS3Keys, setCancelDrawerVoucherS3Keys] = useState<string[]>([]);
    const [cancelDrawerVoucherError, setCancelDrawerVoucherError] = useState(false);

    // 使用 useCurrentWarehouse 获取当前仓库
    const { currentWarehouse: warehouse } = useWarehouseContext();

    // 查询渠道列表
    const { data: channelData, loading: channelLoading, error: channelError } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE, {
        fetchPolicy: 'cache-and-network',
    });

    // 查询异常原因选项
    const { data: exceptionReasonsData, loading: exceptionReasonsLoading, error: exceptionReasonsError } = useQuery<GetPickupExceptionReasonsResponse>(GET_PICKUP_EXCEPTION_REASONS, {
        fetchPolicy: 'cache-and-network',
    });

    // 获取所有州列表
    const { data: statesData, loading: statesLoading, error: statesError } = useQuery<GetAllStatesResponse>(GET_ALL_STATES, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getAllStates) {
                setState(prev => ({
                    ...prev,
                    states: data.getAllStates
                }));
            }
        },
        onError: (error) => {
            message.error(`Failed to load states: ${error.message}`);
        }
    });

    // 根据州获取城市列表
    const [getCitiesByState, { loading: citiesLoading, error: citiesError }] = useLazyQuery<GetCitiesByStateResponse>(GET_CITIES_BY_STATE, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getCitiesByState) {
                setState(prev => ({
                    ...prev,
                    cities: data.getCitiesByState,
                    citiesLoading: false
                }));
            }
        },
        onError: (error) => {
            setState(prev => ({ ...prev, citiesLoading: false }));
            message.error(`Failed to load cities: ${error.message}`);
        }
    });

    // 根据州和城市获取邮编列表
    const [getZipcodesByStateAndCity, { loading: zipcodesLoading, error: zipcodesError }] = useLazyQuery<GetZipcodesByStateAndCityResponse>(GET_ZIPCODES_BY_STATE_AND_CITY, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getZipcodesByStateAndCity) {
                setState(prev => ({
                    ...prev,
                    zipcodes: data.getZipcodesByStateAndCity,
                    zipcodesLoading: false
                }));
            }
        },
        onError: (error) => {
            setState(prev => ({ ...prev, zipcodesLoading: false }));
            message.error(`Failed to load zipcodes: ${error.message}`);
        }
    });

    // 根据邮编获取详细信息
    const [getZipcodeDetail, { loading: zipcodeDetailLoading, error: zipcodeDetailError }] = useLazyQuery<GetZipcodeDetailResponse>(GET_ZIPCODE_DETAIL, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getZipcodeByCode?.timezone) {
                // 在新增模式下，如果仓库已有时区设置，则不覆盖
                // 在详情或编辑模式下，不覆盖已查询到的预约数据中的时区
                if (mode === 'create' && !warehouse?.timezone) {
                    form.setFieldValue('timezone', data.getZipcodeByCode.timezone);
                }
                
                // 自动填充城市和州
                const zipcodeData = data.getZipcodeByCode;
                if (zipcodeData && zipcodeData.city && zipcodeData.state) {
                    form.setFieldsValue({
                        city: zipcodeData.city,
                        state: zipcodeData.state
                    });
                    
                    // 更新选中的州和城市
                    setState(prev => ({
                        ...prev,
                        selectedState: zipcodeData.state || '',
                        selectedCity: zipcodeData.city || ''
                    }));
                    
                    // 加载对应的城市列表
                    if (zipcodeData.state) {
                        getCitiesByState({ variables: { state: zipcodeData.state } });
                    }
                }
            }
        },
        onError: (error) => {
            message.error(`Failed to load zipcode details: ${error.message}`);
        }
    });

    // GraphQL query for warehouse by zipcode
    const [getWarehouseByZipcode, { loading: warehouseQueryLoading }] = useLazyQuery<WarehouseByZipcodeData, WarehouseByZipcodeVariables>(
        GET_WAREHOUSE_BY_ZIPCODE
    );

    // 查询预约详情（编辑或详情模式）
    // 使用 'no-cache'（而非 'network-only'）：
    // 详情接口与列表接口对同字段（如 timezone）返回格式不同（详情=DB 原值 GMT-7/UTC-7，列表=映射后 PT），
    // 若进入 Apollo 归一化缓存会按 __typename + id 覆盖列表行的 timezone，导致打开抽屉后列表显示变化、
    // 刷新页面又恢复。'no-cache' 跳过缓存读写，详情数据只供本组件使用，不污染列表缓存。
    const { data: appointmentData, loading: queryLoading, refetch } = useQuery<PickupAppointmentDetailResponse>(GET_PICKUP_APPOINTMENT_DETAIL, {
        variables: { id: appointmentId },
        skip: !appointmentId || mode === 'create',
        fetchPolicy: 'no-cache', // 不读写归一化缓存，避免详情/列表对同字段不同表达导致列表行被覆盖
        notifyOnNetworkStatusChange: true, // 网络状态变化时通知组件更新
        errorPolicy: 'all' // 即使有错误也返回部分数据
    });

    // 当抽屉打开且有appointmentId时，强制重新请求数据
    useEffect(() => {
        if (visible && appointmentId && mode !== 'create' && refetch) {
            refetch();
        }
    }, [visible, appointmentId, mode, refetch]);

    // GraphQL Mutations
    const [createAppointment] = useMutation<SavePickupAppointmentResponse, { input: SavePickupAppointmentInput }>(SAVE_PICKUP_APPOINTMENT, {
        onCompleted: (data) => {
            message.success('Appointment creation successful！');
            // onSuccess/onClose 统一由 handleSubmit 末尾调用，勿在此重复（曾致 onSuccess 双触发→审核池 resolve+toast 各跑两次）
        },
        onError: (error) => {
            message.error(`Appointment creation failed：${error.message}`);
        }
    });

    // Update the updateAppointment mutation definition to match the expected parameters
    const [updateAppointment] = useMutation<SavePickupAppointmentResponse, { input: SavePickupAppointmentInput }>(SAVE_PICKUP_APPOINTMENT, {
        onCompleted: (data) => {
            message.success('Appointment update successful！');
            // 标记刚刚更新成功，3秒后清除标记
            setJustUpdated(true);
            setTimeout(() => setJustUpdated(false), 3000);
            // onSuccess/onClose 统一由 handleSubmit 末尾调用，勿在此重复
        },
        onError: (error) => {
            const msg = error.message === 'cancel_voucher_required'
                ? t('pickup.appointment.cancelVoucherRequired')
                : `Appointment update failed：${error.message}`;
            message.error(msg);
        }
    });

    const [cancelAppointment, { loading: cancelLoading }] = useMutation<CancelPickupAppointmentResponse, { id: number; cancelVoucherS3Keys?: string[] }>(CANCEL_PICKUP_APPOINTMENT, {
        onCompleted: () => {
            setCancelDrawerModalVisible(false);
            setCancelDrawerVoucherS3Keys([]);
            message.success('Appointment cancellation successful！');
            onSuccess?.();
            onClose();
        },
        onError: (error) => {
            const msg = error.message === 'cancel_voucher_required'
                ? t('pickup.appointment.cancelVoucherRequired')
                : `Cancellation failed：${error.message}`;
            message.error(msg);
        }
    });

    // 检查预约单的查询
    const [checkAppointmentForDate] = useLazyQuery<CheckAppointmentForDateResponse, CheckAppointmentForDateVariables>(
        CHECK_APPOINTMENT_FOR_DATE
    );

    // 处理仓库查询错误
    useEffect(() => {
        if (channelError) {
            message.error('Failed to load channel list: ' + channelError.message);
        }
    }, [channelError]);

    // 处理渠道查询错误
    useEffect(() => {
        if (exceptionReasonsError) {
            message.error('Failed to load exception reasons: ' + exceptionReasonsError.message);
        }
    }, [exceptionReasonsError]);

    // 四级联查处理函数
    const handleStateChange = (state: string) => {
        setState(prev => ({
            ...prev,
            selectedState: state,
            selectedCity: '',
            selectedZipcode: '',
            cities: [],
            zipcodes: [],
            citiesLoading: true
        }));
        
        // 更新表单值
        const formValues: any = {
            state,
            city: undefined,
            zipcode: ''
        };
        
        // 在新增模式下，如果仓库有时区设置，则使用仓库时区；否则重置为默认时区
        if (mode === 'create' && warehouse?.timezone) {
            formValues.timezone = warehouse.timezone;
        }
        
        form.setFieldsValue(formValues);
        
        // 查询城市
        if (state) {
            getCitiesByState({ variables: { state } });
        }
    };

    const handleCityChange = (city: string) => {
        setState(prev => ({
            ...prev,
            selectedCity: city,
            selectedZipcode: '',
            zipcodes: [],
            zipcodesLoading: true
        }));
        
        // 更新表单值
        const formValues: any = {
            city,
            zipcode: ''
        };
        
        // 在新增模式下，如果仓库有时区设置，则使用仓库时区；否则重置为默认时区
        if (mode === 'create' && warehouse?.timezone) {
            formValues.timezone = warehouse.timezone;
        }
        
        form.setFieldsValue(formValues);

        // 查询邮编
        if (state.selectedState && city) {
            getZipcodesByStateAndCity({ variables: { state: state.selectedState, city } });
        }
    };

    // 检查特定日期是否可以创建预约
    const canCreateAppointmentForDate = (date: string, warehouseData: any): boolean => {
        if (!warehouseData || !warehouseData.timezone) {
            return true;
        }

        try {
            // 获取当前 warehouse 时区的时间
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouseData.timezone);
            const selectedDate = dayjs(date);
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');
            
            // 如果选择的是过去的日期，不可以创建
            if (dayjs(date).isBefore(dayjs(today))) {
                return false;
            }
            
            // 如果选择的是未来日期，可以创建
            if (dayjs(date).isAfter(dayjs(today))) {
                return true;
            }
            
            // 如果选择的是今天，检查是否超过截止时间
            if (!warehouseData.appointmentDeadlineTime) {
                return true;
            }
            
            const [hours, minutes] = warehouseData.appointmentDeadlineTime.split(':').map(Number);
            const todayDeadlineInWarehouseZone = currentTimeInWarehouseZone
                .hour(hours)
                .minute(minutes)
                .second(0)
                .millisecond(0);

            return currentTimeInWarehouseZone.isBefore(todayDeadlineInWarehouseZone);
        } catch (error) {
            console.error('Error checking appointment deadline for date:', error);
            return true;
        }
    };

    // 根据 warehouse 时区检查是否可以创建预约（基于列表的 Pickup Date）
    const checkCanCreateAppointment = (warehouseData: any) => {
        // 如果有外部传入的 canCreateAppointment，则不需要内部计算
        if (externalCanCreateAppointment !== undefined) {
            return;
        }

        if (!warehouseData || !warehouseData.timezone) {
            setInternalCanCreateAppointment(true);
            return;
        }

        try {
            // 获取当前 warehouse 时区的时间
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouseData.timezone);
            
            // 获取列表筛选的 Pickup Date。
            // 无 listPickupDate 时回退「hub 时区的今天」而非浏览器本地 dayjs()：下方 today 用的是 hub 时区，
            // 两者口径不一致时（浏览器时区与 hub 跨天，如 EWR 已过午夜而本地仍是前一天）会把当天误判成过去日期。
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');
            const targetDate = listPickupDate || today;

            // 如果目标日期是过去的日期，不能创建预约
            if (dayjs(targetDate).isBefore(dayjs(today))) {
                setInternalCanCreateAppointment(false);
                // 仅在抽屉真正打开时提示：本 effect 依赖里没有 visible，抽屉只要被渲染就会跑，
                // 而审核池详情常驻挂载一个 mode="create" 的抽屉（关闭态），不加 visible 守卫会导致
                // 每次进揽收站点页面都弹一次「date is in the past」
                if (mode === 'create' && visible) {
                    message.info(
                        `Appointments for ${targetDate} cannot be created: this date is in the past. Please select today or a future date.`
                    );
                }
                return;
            }
            
            // 如果目标日期是未来日期，可以创建预约
            if (dayjs(targetDate).isAfter(dayjs(today))) {
                setInternalCanCreateAppointment(true);
                return;
            }
            
            // 如果目标日期是今天，检查是否超过截止时间
            if (!warehouseData.appointmentDeadlineTime) {
                setInternalCanCreateAppointment(true);
                return;
            }
            
            const [hours, minutes] = warehouseData.appointmentDeadlineTime.split(':').map(Number);
            const todayDeadlineInWarehouseZone = currentTimeInWarehouseZone
                .hour(hours)
                .minute(minutes)
                .second(0)
                .millisecond(0);

            const canCreate = currentTimeInWarehouseZone.isBefore(todayDeadlineInWarehouseZone);
            setInternalCanCreateAppointment(canCreate);
            
            // 只在创建模式下、抽屉已打开、且目标日期是今天时显示提示（visible 守卫同上：
            // 审核池详情常驻一个关闭态的 create 抽屉，否则过了截止时间后每次进页面都会弹）
            if (!canCreate && mode === 'create' && visible) {
                message.info(
                    `Current time (${currentTimeInWarehouseZone.format('HH:mm')}) has passed the appointment deadline (${warehouseData.appointmentDeadlineTime}) in ${warehouseData.timezone}. Appointments for ${targetDate} cannot be created. Please select a future date.`
                );
            }
        } catch (error) {
            console.error('Error checking appointment deadline:', error);
            setInternalCanCreateAppointment(true);
        }
    };

    // 自动计算基于当前仓库的最小取件日期
    useEffect(() => {
        if (!warehouse || !warehouse.id) return;
        
        // 检查是否可以创建预约
        checkCanCreateAppointment(warehouse);
        
        // 计算基于当前仓库的最小取件日期
        // 使用列表页面的筛选日期作为基准日期，保持一致性
        let minPickupDate: dayjs.Dayjs;
        let deadlineTime = null;
        
        if (listPickupDate) {
            // 如果有列表筛选日期，使用该日期作为基准
            minPickupDate = dayjs(listPickupDate);
        } else {
            // 否则根据截止时间判断最小日期
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone || 'UTC');
            
            if (warehouse.appointmentDeadlineTime && warehouse.timezone) {
                // Parse deadline time (format: "HH:mm")
                const [hours, minutes] = warehouse.appointmentDeadlineTime.split(':').map(Number);
                const todayDeadlineInWarehouseZone = currentTimeInWarehouseZone
                    .hour(hours)
                    .minute(minutes)
                    .second(0)
                    .millisecond(0);

                // 如果当前时间超过了今天的截止时间，最小取件日期是明天
                if (currentTimeInWarehouseZone.isAfter(todayDeadlineInWarehouseZone)) {
                    minPickupDate = currentTimeInWarehouseZone.add(1, 'day').startOf('day');
                } else {
                    // 否则最小取件日期是今天
                    minPickupDate = currentTimeInWarehouseZone.startOf('day');
                }
            } else {
                // 没有截止时间设置，默认今天
                minPickupDate = currentTimeInWarehouseZone.startOf('day');
            }
        }
        
        if (warehouse.appointmentDeadlineTime && warehouse.timezone) {
            deadlineTime = warehouse.appointmentDeadlineTime;
            setAppointmentDeadlineTime(deadlineTime);
        } else {
            setAppointmentDeadlineTime(null);
        }
        
        setMinimumPickupDate(minPickupDate);
        
        // 在创建模式下，更新表单中的最小取件日期
        if (mode === 'create') {
            form.setFieldValue('expectedPickupDate', minPickupDate);
        }
        // visible 必须在依赖里：提示已加 visible 守卫，若不随 visible 重跑，抽屉后续打开时该提示就永远不出
    }, [warehouse, mode, form, listPickupDate, visible]);

    // 内部zipcode处理函数，支持静默模式
    const handleZipcodeChangeInternal = async (zipcode: string, silent: boolean = false) => {
        setState(prev => ({
            ...prev,
            selectedZipcode: zipcode
        }));
        
        // 更新表单值
        form.setFieldValue('zipcode', zipcode);

        // 查询时区并自动填充
        if (zipcode) {
            getZipcodeDetail({ variables: { zipcode } });
            
            // Query warehouses by zipcode
            try {
                const result = await getWarehouseByZipcode({
                    variables: { zipcode }
                });

                const warehouses = result.data?.getWarehouseByZipcode || [];

                // 只有在非静默模式下才显示提示
                if (!silent) {
                    if (warehouses.length === 0) {
                        // 只有在非"刚刚更新成功"状态下才显示"未找到仓库"提示
                        if (!justUpdated) {
                            console.log('No HUB found for zipcode:', zipcode);
                            message.info(`No specific HUB found for zipcode ${zipcode}`);
                        }
                    } else if (warehouses.length === 1) {
                        // Exactly one warehouse found, auto-select it
                        const warehouse = warehouses[0];
                        const currentWarehouseId = form.getFieldValue('warehouseId');
                        
                        // Check if current selection matches
                        if (currentWarehouseId && currentWarehouseId !== parseInt(warehouse.id)) {
                            // Current selection doesn't match, show error message
                            message.error(`Zipcode ${zipcode} belongs to HUB "${warehouse.name}". Please select the correct HUB.`);
                        } else {
                            // Auto-select the matching warehouse
                            form.setFieldValue('warehouseId', parseInt(warehouse.id));
                            message.success(`HUB "${warehouse.name}" automatically selected for zipcode ${zipcode}.`);
                        }
                    }
                }
            } catch (error) {
                console.error('Error querying HUB by zipcode:', error);
                if (!silent) {
                    message.error('Failed to query HUB for this zipcode');
                }
            }
        }
    };

    // 对外的zipcode变化处理函数
    const handleZipcodeChange = async (zipcode: string) => {
        await handleZipcodeChangeInternal(zipcode, false);
    };

    // 处理编辑模式下的表单数据填充
    const setAppointmentData = (data: any) => {
        if (data) {
            form.setFieldsValue({
                id: data.id,
                warehouseId: data.warehouseId ? parseInt(data.warehouseId.toString()) : null,
                channelId: data.channelId ? parseInt(data.channelId.toString()) : null,
                cooperationWarehouseId: data.cooperationWarehouseId ? parseInt(data.cooperationWarehouseId.toString()) : null,
                cooperationWarehouseName: data.cooperationWarehouseName || '',
                appointmentNumber: data.appointmentNumber || '',
                contactName: data.contactName,
                contactPhone: data.contactPhone,
                countryCode: data.countryCode || 1,
                contactEmail: data.contactEmail,
                ext: data.ext,
                addressLine1: data.addressLine1,
                addressLine2: data.addressLine2,
                country: data.country || 'US',
                state: data.state || '',
                city: data.city || '',
                zipcode: data.zipcode,
                timezone: data.timezone,
                workWeekdaysJson: data.workWeekdaysJson,
                workStartTime: data.workStartTime ? dayjs(data.workStartTime, 'HH:mm') : null,
                workEndTime: data.workEndTime ? dayjs(data.workEndTime, 'HH:mm') : null,
                expectedPickupDate: data.expectedPickupDate ? dayjs(data.expectedPickupDate) : null,
                expectedPickupTimeStart: data.expectedPickupTimeStart ? dayjs(data.expectedPickupTimeStart, 'HH:mm') : null,
                expectedPickupTimeEnd: data.expectedPickupTimeEnd ? dayjs(data.expectedPickupTimeEnd, 'HH:mm') : null,
                actualPickupDate: data.actualPickupDate ? dayjs(data.actualPickupDate) : null,
                actualPickupTimeStart: data.actualPickupTimeStart ? dayjs(data.actualPickupTimeStart, 'HH:mm') : null,
                actualPickupTimeEnd: data.actualPickupTimeEnd ? dayjs(data.actualPickupTimeEnd, 'HH:mm') : null,
                // `|| undefined`：详情返回的是**展示口径**值——未手动设置过的单给的是挂载运单数，
                // 没挂运单时就是 0。回填 0 会被 InputNumber 的 min={1} 在失焦时钳成 1，
                // 这个钳制会触发 onChange 把字段标记为 touched，保存时就把 1 传上去，
                // 后端据此置 expected_parcel_count_manual=1 —— 运营只是点了下别的输入框，
                // 这张单的预期数量就被永久冻结、不再跟随挂载运单数。2026-09-20 自测实测。
                // 转成 undefined 后输入框为空，不参与钳制，也就不会被误标记。
                expectedParcelCount: data.expectedParcelCount || undefined,
                actualParcelCount: data.actualParcelCount,
                expectedParcelUnit: data.expectedParcelUnit ? data.expectedParcelUnit.toString() : undefined,
                actualParcelUnit: data.actualParcelUnit ? data.actualParcelUnit.toString() : undefined,
                // 为空表示未单独指定，由抽屉带出站点当日排期车型作为默认值
                vehicleTypeId: data.vehicleTypeId ?? undefined,
                notes: data.notes,
                trackingNumbersJson: data.trackingNumbersJson || [],
                // 上门扫描单号只读展示：保存时构造的是显式字段，不会被提交
                scanTrackingNumbers: data.scanTrackingNumbers || [],
                pickupSerialNosJson: data.pickupSerialNosJson || [],
                status: data.status,
                manualReason: data.manualReason || DEFAULT_MANUAL_REASON,
                isFinished: data.isFinished,
                isAbnormal: data.isAbnormal,
                exceptionReasonCode: data.exceptionReasonCode,
                photosS3KeysJson: data.photosS3KeysJson || [],
                photosS3UrlJson: data.photosS3UrlJson || [],
                exceptionPhotosS3KeysJson: data.exceptionPhotosS3KeysJson || [],
                cancelVoucherS3Keys: data.cancelVoucherS3KeysJson || [],
            });
        }
    };

    // Update the useEffect that loads appointment data
    useEffect(() => {
        if (appointmentData?.getPickupAppointmentDetail && 
            channelData?.getAllChannelsLite && 
            warehouse) {
            const data = appointmentData.getPickupAppointmentDetail;

            setState(prev => ({
                ...prev,
                appointment: data as any
            }));

            // 设置四级联查的状态
            if (data.state) {
                setState(prev => ({
                    ...prev,
                    selectedState: data.state || ''
                }));
                // 查询对应的城市
                getCitiesByState({ variables: { state: data.state } });
            }
            if (data.city && data.state) {
                setState(prev => ({
                    ...prev,
                    selectedCity: data.city || ''
                }));
                // 查询对应的邮编
                getZipcodesByStateAndCity({ variables: { state: data.state, city: data.city } });
            }
            // 设置表单数据
            setAppointmentData(data);
            
            if (data.zipcode) {
                setState(prev => ({ ...prev, selectedZipcode: data.zipcode }));
                // 在详情和编辑模式下，不需要触发zipcode查询，因为已经有完整数据
                // 只在需要验证仓库匹配时才触发查询
                if (mode === 'edit') {
                    handleZipcodeChangeInternal(data.zipcode, true);
                }
            }
        }
    }, [appointmentData, channelData, warehouse, form, getCitiesByState, getZipcodesByStateAndCity]);

    // 初始化表单默认值（新增模式）
    useEffect(() => {
        if (mode === 'create' && visible) {
            // 重置所有状态
            setState(prev => ({
                ...prev,
                appointment: null,
                selectedState: '',
                selectedCity: '',
                selectedZipcode: '',
                cities: [],
                zipcodes: []
            }));
            
            // 重置表单，使用表单组件中的默认值
            form.resetFields();
            
            // 设置当前仓库 ID 和时区
            if (warehouse?.id) {
                form.setFieldValue('warehouseId', parseInt(warehouse.id.toString()));
                
                // 根据仓库设置默认时区
                if (warehouse.timezone) {
                    form.setFieldValue('timezone', warehouse.timezone);
                }
            }
            
            // 设置循环预约的默认日期，优先使用列表筛选日期，保持一致性
            const defaultDate = listPickupDate || (minimumPickupDate ? minimumPickupDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
            form.setFieldValue('selectedDates', [defaultDate]);
        }
    }, [mode, visible, form, warehouse, listPickupDate, minimumPickupDate]);

    // 组件关闭时重置状态
    useEffect(() => {
        if (!visible) {
            setJustUpdated(false);
        }
    }, [visible]);

    // 创建预约输入对象的通用函数
    const createAppointmentInput = (values: PickupAppointmentFormData, pickupDate?: string, isNeedMerge?: boolean): SavePickupAppointmentInput => {
        return {
            // 如果是编辑模式，使用表单中的ID或appointmentId
            ...(mode !== 'create' && {
                id: values.id || appointmentId
            }),
            // 复揽建单：携带原疑似虚假单 ID（仅创建模式），供疑似虚假精确判定已完结
            ...(mode === 'create' && initialRepickupSourceId != null && {
                repickupSourceAppointmentId: initialRepickupSourceId
            }),
            warehouseId: values.warehouseId,
            channelId: values.channelId,
            cooperationWarehouseId: values.cooperationWarehouseId,
            appointmentNumber: values.appointmentNumber || '',
            contactName: values.contactName,
            contactPhone: values.contactPhone,
            countryCode: values.countryCode || 1,
            contactEmail: values.contactEmail,
            ext: values.ext,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            country: values.country || 'US',
            state: values.state || '',
            city: values.city || '',
            zipcode: values.zipcode,
            timezone: values.timezone,
            workWeekdaysJson: values.workWeekdaysJson,
            workStartTime: values.workStartTime ? dayjs(values.workStartTime).format('HH:mm') : '09:00',
            workEndTime: values.workEndTime ? dayjs(values.workEndTime).format('HH:mm') : '18:00',
            expectedPickupDate: pickupDate || (values.expectedPickupDate ? dayjs(values.expectedPickupDate).format('YYYY-MM-DD') : ''),
            expectedPickupTimeStart: values.expectedPickupTimeStart ? dayjs(values.expectedPickupTimeStart).format('HH:mm') : '',
            expectedPickupTimeEnd: values.expectedPickupTimeEnd ? dayjs(values.expectedPickupTimeEnd).format('HH:mm') : '',
            actualPickupDate: values.actualPickupDate ? dayjs(values.actualPickupDate).format('YYYY-MM-DD') : undefined,
            actualPickupTimeStart: values.actualPickupTimeStart ? dayjs(values.actualPickupTimeStart).format('HH:mm') : undefined,
            actualPickupTimeEnd: values.actualPickupTimeEnd ? dayjs(values.actualPickupTimeEnd).format('HH:mm') : undefined,
            // 预期数量：只有用户真正动过这个输入框才上传，后端据此标记「手动设置过」并以该值展示。
            // - 新建：字段无默认值且必填，用户必然输入 ⇒ 必然上传，manual=1
            // - 编辑：没动过就不传，保持原值与原标记，展示继续取挂载运单数 —— 与改造前一致
            // 不能直接传 values.expectedParcelCount：提交的是全量 values，
            // 只改个联系人保存，也会把「跟随挂载运单数」的存量单静默固化成当前显示值。
            // （form.setFieldValue 不标记 touched，编辑态回填不会误判）
            expectedParcelCount: form.isFieldTouched('expectedParcelCount')
                ? (values.expectedParcelCount || 1)
                : undefined,
            actualParcelCount: values.actualParcelCount || 0,
            expectedParcelUnit: values.expectedParcelUnit || undefined,
            actualParcelUnit: values.actualParcelUnit || undefined,
            // 车型分两种语义，不能合并成一句：
            // - 新建：抽屉带出的站点当日车型就是 PRD 要的「快照」，必须落库；
            // - 编辑：只有用户真的动过这个下拉才写。否则改个手机号保存一下，就会把一张
            //   vehicle_type_id 为空、本来动态跟随站点排期的存量单，静默冻结成当下的快照值——
            //   灰度放量或站点改排期后，这批单会停在旧车型，和同站点同一天的其它单显示不一致。
            //   坑与上面 expectedParcelCount 同款（form.setFieldValue 不会标记 touched，预填不会误判）。
            vehicleTypeId: mode === 'create'
                ? (values.vehicleTypeId ?? undefined)
                : (form.isFieldTouched('vehicleTypeId') ? values.vehicleTypeId : undefined),
            notes: values.notes,
            trackingNumbersJson: (() => {
                if (typeof values.trackingNumbersJson === 'string' && values.trackingNumbersJson.trim()) {
                    // 支持多种分隔符：换行符、逗号、分号
                    return values.trackingNumbersJson
                        .split(/[\n,;]+/)
                        .map((item: string) => item.trim())
                        .filter(Boolean);
                }
                return Array.isArray(values.trackingNumbersJson) ? values.trackingNumbersJson : [];
            })(),
            pickupSerialNosJson: (() => {
                if (typeof values.pickupSerialNosJson === 'string' && values.pickupSerialNosJson.trim()) {
                    // 支持多种分隔符：换行符、逗号、分号
                    return values.pickupSerialNosJson
                        .split(/[\n,;]+/)
                        .map((item: string) => item.trim())
                        .filter(Boolean);
                }
                return Array.isArray(values.pickupSerialNosJson) ? values.pickupSerialNosJson : [];
            })(),
            status: values.status,
            isFinished: values.isFinished || false,
            isAbnormal: Boolean(values.isAbnormal),
            exceptionReasonCode: values.exceptionReasonCode,
            photosS3KeysJson: values.photosS3KeysJson || [],
            exceptionPhotosS3KeysJson: values.exceptionPhotosS3KeysJson || [],
            cancelVoucherS3Keys: values.cancelVoucherS3Keys,
            isNeedMerge: isNeedMerge !== undefined ? isNeedMerge : undefined,
            // 手动加单原因：始终传非空值，避免后端 NonNull (PickupAppointmentManualReason!) 校验失败。
            // 编辑模式下若该字段未渲染（source != MANUAL），Form 状态可能为 null，
            // 此时回退到 state.appointment 的原值，最终兜底 DEFAULT_MANUAL_REASON。
            // 编辑场景对此字段的"是否覆盖"应由后端 update 逻辑判定，不依赖前端是否传递。
            manualReason: (
                values.manualReason ||
                state.appointment?.manualReason ||
                DEFAULT_MANUAL_REASON
            ) as PickupAppointmentManualReason,
        };
    };

    // 执行提交逻辑的辅助函数，支持设置 isNeedMerge
    const doSubmitWithIsNeedMerge = async (values: any, isNeedMergeForToday?: boolean) => {
        const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse?.timezone || 'UTC');
        const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');

        // 处理多日期预约
        if (mode === 'create' && values.selectedDates && values.selectedDates.length > 0) {
            const dates = values.selectedDates;

            // 先执行第一个预约创建
            const firstDateStr = dates[0];
            form.setFieldValue('expectedPickupDate', dayjs(firstDateStr));
            const firstIsNeedMerge = firstDateStr === today ? isNeedMergeForToday : undefined;
            const firstInput = createAppointmentInput(values, firstDateStr, firstIsNeedMerge);

            try {
                const res = await createAppointment({ variables: { input: firstInput } });
                if (!res?.data || res?.errors?.length) {
                    const errorMessages = res.errors?.map(e => e.message).join('; ') || 'Unknown error';
                    throw new Error(`Appointment creation failed: ${errorMessages}`);
                }

                // 如果第一个成功，继续创建剩余的预约
                if (dates.length > 1) {
                    const remainingPromises = [];

                    for (let i = 1; i < dates.length; i++) {
                        const dateStr = dates[i];
                        form.setFieldValue('expectedPickupDate', dayjs(dateStr));
                        const currentIsNeedMerge = dateStr === today ? isNeedMergeForToday : undefined;
                        const currentInput = createAppointmentInput(values, dateStr, currentIsNeedMerge);
                        remainingPromises.push(createAppointment({ variables: { input: currentInput } }));
                    }

                    await Promise.all(remainingPromises);
                }

                onSuccess?.();
                onClose();
            } catch (error) {
                console.error('Appointment creation failed:', error);
                throw error;
            }
        } else {
            // 单日期预约处理
            const expectedPickupDate = values.expectedPickupDate ? dayjs(values.expectedPickupDate).format('YYYY-MM-DD') : null;
            const isNeedMerge = expectedPickupDate === today ? isNeedMergeForToday : undefined;
            const input = createAppointmentInput(values, undefined, isNeedMerge);

            if (mode === 'create') {
                await createAppointment({ variables: { input } });
            } else if (mode === 'edit' || mode === 'detail') {
                if (input.id === undefined) {
                    console.error('Missing ID for update operation');
                    message.error('Update failed: Missing reservation ID');
                    return;
                }
                await updateAppointment({ variables: { input } });
            }

            onSuccess?.();
            onClose();
        }
    };

    // Update the handleSubmit function to ensure ID is properly handled
    const handleSubmit = async (values: any) => {
        try {
            setState(prev => ({ ...prev, loading: true }));

            // 在创建模式下，检查选择的日期是否可以创建预约
            if (mode === 'create' && warehouse) {
                const selectedDates = values.selectedDates || [];
                const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone || 'UTC');
                const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');
                
                const invalidDates = selectedDates.filter((date: string) => 
                    !canCreateAppointmentForDate(date, warehouse)
                );
                
                if (invalidDates.length > 0) {
                    // 分类无效日期
                    const pastDates = invalidDates.filter((date: string) => dayjs(date).isBefore(dayjs(today)));
                    const todayDates = invalidDates.filter((date: string) => dayjs(date).isSame(dayjs(today)));
                    
                    let errorMessage = '';
                    if (pastDates.length > 0 && todayDates.length > 0) {
                        errorMessage = `Cannot create appointments: ${pastDates.join(', ')} are in the past, and ${todayDates.join(', ')} has passed the appointment deadline (${warehouse.appointmentDeadlineTime}). Please select future dates only.`;
                    } else if (pastDates.length > 0) {
                        errorMessage = `Cannot create appointments for ${pastDates.join(', ')}: these dates are in the past. Please select today or future dates.`;
                    } else if (todayDates.length > 0) {
                        errorMessage = `Cannot create appointments for ${todayDates.join(', ')}: current time (${currentTimeInWarehouseZone.format('HH:mm')}) has passed the appointment deadline (${warehouse.appointmentDeadlineTime}). Please select future dates only.`;
                    }
                    
                    message.error(errorMessage);
                    setState(prev => ({ ...prev, loading: false }));
                    return;
                }
            }

            // 在创建模式下，检查STANDING_PICKUP类型的合作仓库当天是否已存在预约单
            if (mode === 'create' && values.cooperationWarehouseId && warehouse) {
                const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone || 'UTC');
                const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');

                // 获取选择的日期列表
                const selectedDates = values.selectedDates || [];
                const expectedPickupDate = values.expectedPickupDate ? dayjs(values.expectedPickupDate).format('YYYY-MM-DD') : null;

                // 确定需要检查的日期：循环预约时检查全部所选日期（含 today 与未来日期），
                // 单日期时仅检查 expectedPickupDate。
                // 修复：以前多日期只检查 today，未来日期完全不查；非常规加单原因在「未来日期当日无单」
                // 场景下被前端漏过，最终由 api-server 老路径放行。
                const datesToCheck: string[] = selectedDates.length > 0
                    ? [...selectedDates]
                    : (expectedPickupDate ? [expectedPickupDate] : []);

                // 检查日期是否存在预约单
                if (datesToCheck.length > 0) {
                    try {
                        // 并发查询每个日期，按合作仓 + 日期判断当日是否已有预约单
                        const checkResults = await Promise.all(
                            datesToCheck.map(date => checkAppointmentForDate({
                                variables: {
                                    cooperationWarehouseId: values.cooperationWarehouseId,
                                    expectedPickupDate: date
                                }
                            }))
                        );

                        const existsList = checkResults.map(r => r.data?.checkAppointmentForDate?.exists ?? false);
                        // 多日期场景下：special 原因要求每个日期都有单（任一无单即拦截，严格于「任一有单就放行」）。
                        const exists = existsList.every(Boolean);
                        const isSpecialReason = SPECIAL_MANUAL_REASONS.includes(values.manualReason || DEFAULT_MANUAL_REASON);

                        // 确认后继续创建的对话框（当日已有单）：复用现有「是否继续创建」文案
                        const confirmContinueCreate = () => new Promise<void>((resolve) => {
                            Modal.confirm({
                                title: t('pickup.appointment.appointmentPageDetails.messages.confirmCreateAppointmentTitle'),
                                content: t('pickup.appointment.appointmentPageDetails.messages.confirmCreateAppointmentContent'),
                                okText: t('pickup.appointment.appointmentPageDetails.messages.continueCreate'),
                                cancelText: t('components.button.cancel'),
                                okButtonProps: {
                                    style: {
                                        backgroundColor: '#722ed1',
                                        borderColor: '#722ed1'
                                    }
                                },
                                cancelButtonProps: {
                                    style: {
                                        color: '#722ed1',
                                        borderColor: '#722ed1'
                                    }
                                },
                                onOk: async () => {
                                    try {
                                        // 用户确认后，设置 isNeedMerge 为 false，然后继续执行提交逻辑
                                        await doSubmitWithIsNeedMerge(values, false);
                                        resolve();
                                    } catch (error) {
                                        console.error('Submission failed:', error);
                                        resolve();
                                    } finally {
                                        setState(prev => ({ ...prev, loading: false }));
                                    }
                                },
                                onCancel: () => {
                                    setState(prev => ({ ...prev, loading: false }));
                                    resolve();
                                }
                            });
                        });

                        // 特殊加单原因（失败重揽/部分补遗/超载分批）：对所有站点一致（含 standingPickup，不豁免）
                        //   当日有单 → 弹「是否继续创建」确认后创建；当日无单 → 提示「请选常规上门」并阻止
                        if (isSpecialReason) {
                            if (exists) {
                                return confirmContinueCreate();
                            }
                            Modal.warning({
                                title: t('pickup.appointment.appointmentPageDetails.messages.manualReasonNoExistingTitle'),
                                content: t('pickup.appointment.appointmentPageDetails.messages.manualReasonNoExistingContent'),
                            });
                            setState(prev => ({ ...prev, loading: false }));
                            return;
                        }

                        // 常规上门：不再弹「是否继续创建」。后端已统一按「同站点同日唯一」硬拦（含 standingPickup，
                        // 需求点3），当日已有单时直接提交由后端拦截并返回本地化提示，行为与非 standing 站点一致。
                    } catch (error) {
                        console.error('检查预约单是否存在时出错:', error);
                        // 检查失败时，继续执行创建流程，不阻塞用户操作
                    }
                }
            }

            // 处理多日期预约
            if (mode === 'create' && values.selectedDates && values.selectedDates.length > 0) {
                // 如果是多日期预约，则创建多个预约
                if (values.selectedDates.length > 0) {
                    const dates = values.selectedDates;
                    
                    // 先执行第一个预约创建作为测试
                    const firstDateStr = dates[0];
                    form.setFieldsValue({ expectedPickupDate: dayjs(firstDateStr) });
                    const firstInput = createAppointmentInput(values, firstDateStr);
                    
                    try {
                        // 执行第一个预约创建
                        const res = await createAppointment({ variables: { input: firstInput } })
                        if (!res?.data || res?.errors?.length) {
                            const errorMessages = res.errors?.map(e => e.message).join('; ') || 'Unknown error';
                            throw new Error(`Appointment creation failed: ${errorMessages}`);
                        }

                        // 如果第一个成功，继续创建剩余的预约
                        if (dates.length > 1) {
                            const remainingPromises = [];
                            
                            for (let i = 1; i < dates.length; i++) {
                                const dateStr = dates[i];
                                form.setFieldsValue({ expectedPickupDate: dayjs(dateStr) });
                                const currentInput = createAppointmentInput(values, dateStr);
                                remainingPromises.push(createAppointment({ variables: { input: currentInput } }));
                            }
                            
                            // 并行执行剩余的预约创建
                            await Promise.all(remainingPromises);
                        }
                        
                        onSuccess?.();
                        onClose();
                        return;
                    } catch (error) {
                        // 第一个预约创建失败，不继续执行剩余的
                        console.error('First appointment creation failed, stopping batch creation:', error);
                        throw error; // 重新抛出错误，让外层的 catch 处理
                    }
                }
            }
            
            // 如果没有需要确认的情况，使用默认的 isNeedMerge（undefined，后端会使用默认值 true）
            // 对于编辑模式，直接使用原有逻辑
            if (mode === 'edit' || mode === 'detail') {
                // // 取消状态的预约单更新时，取消凭证不能为空
                // if (state.appointment?.status === 'CANCELLED' &&
                //     (!values.cancelVoucherS3Keys || values.cancelVoucherS3Keys.length === 0)) {
                //     message.error(t('pickup.appointment.cancelVoucherRequired'));
                //     setState(prev => ({ ...prev, loading: false }));
                //     return;
                // }
                const input = createAppointmentInput(values);
                if (input.id === undefined) {
                    console.error('Missing ID for update operation');
                    message.error('Update failed: Missing reservation ID');
                    setState(prev => ({ ...prev, loading: false }));
                    return;
                }
                await updateAppointment({ variables: { input } });
                onSuccess?.();
                onClose();
            } else {
                // 创建模式，使用统一的提交函数（传入 undefined 表示使用默认值 true）
                await doSubmitWithIsNeedMerge(values, undefined);
            }
        } catch (error) {
            console.error('Submission failed:', error);
        } finally {
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    // 判断是否可以取消
    const isCancelable = (status?: string) => {
        if (!status) return false;
        const cancelableStatuses = ['CREATED', 'DISPATCHING', 'DISPATCHED', 'PICKING_UP'];
        return cancelableStatuses.includes(status);
    };

    // 根据预约单状态确定字段是否可编辑
    const getFieldEditability = (fieldName: string): boolean => {
        // 审核池「确认预约单」：全字段只读（resolve 不保存表单编辑，避免可改但被丢弃的误导）。
        if (reviewConfirmMode) return false;
        if (mode === 'create') return true; // 新建时所有字段都可编辑

        const status = state.appointment?.status;
        if (!status) return false; // 详情模式下，如果状态不明确，默认不可编辑

        // 根据状态和字段名确定编辑权限
        switch (status) {
            case 'CREATED': // 已创建
                // 客户信息不可更改，合作仓库可更改，联系方式可更改，揽收地址可更改，揽收时间可更改，包裹数量可更改，包裹明细可更改
                const createdEditableFields = [
                    'cooperationWarehouseId', // 合作仓库可编辑
                    'contactName','contactPhone', 'countryCode', 'ext', 'contactEmail', // 联系方式
                    'addressLine1', 'addressLine2', 'state', 'city', 'zipcode', // 揽收地址
                    'workWeekdaysJson', 'workStartTime', 'workEndTime', 'timezone', // 揽收时间
                    'expectedPickupTimeStart', 'expectedPickupTimeEnd',
                    'expectedParcelCount', 'expectedParcelUnit', // 包裹数量
                    // 车型只在已创建时可改：进入调度后车辆已按车型分组派出，此时改只会让列表与实际派车不符
                    'vehicleTypeId',
                    'notes', // 包裹明细
                    'trackingNumbersJson', // 追踪号码在已创建状态下可编辑
                    'pickupSerialNosJson' // 提货流水号在已创建状态下可编辑
                ];
                return createdEditableFields.includes(fieldName);

            case 'DISPATCHING': // 调度中
                // 客户信息不可更改，合作仓库可更改，联系方式可更改，揽收地址不可更改，揽收时间可更改，包裹数量可更改，包裹明细可更改
                const dispatchingEditableFields = [
                    'cooperationWarehouseId', // 合作仓库可编辑
                    'contactName','contactPhone', 'countryCode', 'ext', 'contactEmail'
                ];
                return dispatchingEditableFields.includes(fieldName);

            case 'DISPATCHED': // 已调度
                // 客户信息和合作仓库不可更改，联系方式可更改，揽收地址不可更改，揽收时间不可更改，包裹数量可更改，包裹明细可更改
                const dispatchedEditableFields = [
                    'contactName','contactPhone', 'countryCode', 'ext', 'contactEmail'
                ];
                return dispatchedEditableFields.includes(fieldName);

            case 'PICKING_UP': // 进行中
                // 客户信息和合作仓库不可更改，联系方式可更改，揽收地址不可更改，揽收时间不可更改，包裹数量可更改，包裹明细可更改
                const pickingUpEditableFields = [
                    'contactName','contactPhone', 'countryCode', 'ext', 'contactEmail'
                ];
                return pickingUpEditableFields.includes(fieldName);

            case 'PICKED_UP': // 已取件/已失败/已成功
            case 'PICKUP_FAILED':
            case 'CANCELLED':
                // 所有字段都不可更改
                return false;

            default:
                return false; // 未知状态下默认不可编辑
        }
    };

    // 判断字段组是否可编辑（用于复合字段）
    const isFieldGroupEditable = (groupName: string): boolean => {
        if (reviewConfirmMode) return false;
        if (mode === 'create') return true;

        const status = state.appointment?.status;
        if (!status) return true;

        switch (groupName) {
            case 'customer': // 客户信息组
                return getFieldEditability('channelId');
            case 'contact': // 联系方式组
                return getFieldEditability('contactPhone');
            case 'address': // 揽收地址组
                return getFieldEditability('addressLine1');
            case 'workTime': // 揽收时间组
                return getFieldEditability('workStartTime');
            case 'pickupTime': // 期望揽收时间组
                return getFieldEditability('expectedPickupTimeStart');
            case 'parcel': // 包裹信息组
                return getFieldEditability('expectedParcelCount');
            default:
                return true;
        }
    };

    // 获取渠道显示名称
    const getChannelDisplayName = (channelId: number | null): string => {
        if (!channelId || !channelData?.getAllChannelsLite) {
            return channelId ? `Channel ${channelId}` : '';
        }
        const channel = channelData.getAllChannelsLite.find((channel) => parseInt(channel.id) === channelId);
        return channel?.channelName || `Channel ${channelId}`;
    };

    // 获取仓库显示名称
    const getWarehouseDisplayName = (inputWarehouseId: number | null): string => {
        if (!inputWarehouseId || !warehouse) {
            return inputWarehouseId ? `Warehouse ${inputWarehouseId}` : '';
        }
        // 如果传入的 warehouseId 与当前仓库 ID 匹配，返回仓库名称
        if (warehouse.id && parseInt(warehouse.id.toString()) === inputWarehouseId) {
            return warehouse.name || `Warehouse ${inputWarehouseId}`;
        }
        return `Warehouse ${inputWarehouseId}`;
    };

    const handleCancelAppointment = () => {
        setCancelDrawerVoucherS3Keys([]);
        setCancelDrawerVoucherError(false);
        setCancelDrawerModalVisible(true);
    };

    const handleConfirmDrawerCancel = () => {
        // if (cancelDrawerVoucherS3Keys.length === 0) {
        //     setCancelDrawerVoucherError(true);
        //     return;
        // }
        cancelAppointment({ variables: { id: appointmentId!, cancelVoucherS3Keys: cancelDrawerVoucherS3Keys } });
    };

    const getTitle = () => {
        switch (mode) {
            case 'create':
                return t('pickup.appointment.appointmentPageDetails.titles.createAppointment');
            case 'detail':
                return t('pickup.appointment.appointmentPageDetails.titles.details');
            default:
                return t('pickup.appointment.appointmentPageDetails.titles.info');
        }
    };

    return {
        form,
        state,
        channelData,
        channelLoading,
        exceptionReasonsData,
        exceptionReasonsLoading,
        queryLoading,
        citiesLoading,
        zipcodesLoading,
        statesLoading,
        appointmentData,
        warehouseQueryLoading,
        appointmentDeadlineTime,
        minimumPickupDate,
        canCreateAppointment,
        handleStateChange,
        handleCityChange,
        handleZipcodeChange,
        handleSubmit,
        handleCancelAppointment,
        handleConfirmDrawerCancel,
        cancelDrawerModalVisible,
        setCancelDrawerModalVisible,
        cancelDrawerVoucherS3Keys,
        setCancelDrawerVoucherS3Keys,
        cancelDrawerVoucherError,
        setCancelDrawerVoucherError,
        cancelLoading,
        isCancelable,
        getFieldEditability,
        isFieldGroupEditable,
        getTitle,
        getChannelDisplayName,
        getWarehouseDisplayName
    };
}; 